import React from 'react';
import { X } from 'lucide-react';
import './VersionComparisonModal.css';
// ── Robust tag-preserving formatting-aware word-level diff helper ────────────
import { diffArrays } from 'diff';

function escapeHtml(text) {
    if (!text) return '';
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

const FORMATTING_ALIASES = {
    'b': 'strong',
    'i': 'em',
    's': 'del',
    'strike': 'del'
};

const INLINE_FORMATTING_TAGS = new Set([
    'strong', 'em', 'u', 'code', 'del', 'ins'
]);

function unescapeHTML(str) {
    if (!str) return '';
    return str.replace(/&amp;/g, '&')
              .replace(/&lt;/g, '<')
              .replace(/&gt;/g, '>')
              .replace(/&quot;/g, '"')
              .replace(/&#039;/g, "'")
              .replace(/&nbsp;/g, ' ');
}

function normalizeTag(tagStr) {
    if (!tagStr) return '';
    const isClosing = tagStr.startsWith('</');
    const match = tagStr.match(/<\/?([a-zA-Z0-9:-]+)/);
    const tagName = match ? match[1].toLowerCase() : '';
    if (!tagName) return tagStr;
    return isClosing ? `</${tagName}>` : `<${tagName}>`;
}

function tokenizeHtmlForDiff(html) {
    const tokens = [];
    let i = 0;
    const activeFormatting = [];

    while (i < html.length) {
        if (html[i] === '<') {
            let j = i;
            while (j < html.length && html[j] !== '>') j++;
            const tag = html.substring(i, j + 1);

            const isClosing = tag.startsWith('</');
            const tagNameMatch = tag.match(/<\/?([a-zA-Z0-9]+)/);
            const tagNameRaw = tagNameMatch ? tagNameMatch[1].toLowerCase() : '';
            const tagName = FORMATTING_ALIASES[tagNameRaw] || tagNameRaw;

            if (INLINE_FORMATTING_TAGS.has(tagName)) {
                if (isClosing) {
                    const idx = activeFormatting.lastIndexOf(tagName);
                    if (idx !== -1) {
                        activeFormatting.splice(idx, 1);
                    }
                } else {
                    activeFormatting.push(tagName);
                }
            }

            tokens.push({ type: 'tag', value: tag });
            i = j + 1;
        } else {
            let j = i;
            while (j < html.length && html[j] !== '<') j++;
            const textContent = html.substring(i, j);
            
            const formatSuffix = activeFormatting.slice().sort().join(',');

            // Unescape HTML entities first so they don't break diffing
            const unescapedText = unescapeHTML(textContent);
            const parts = unescapedText.match(/\s+|[\p{L}\p{N}_]+|[^\p{L}\p{N}_\s]+/gu) || [];
            parts.forEach(part => {
                tokens.push({ 
                    type: 'text', 
                    value: part, 
                    formatting: formatSuffix
                });
            });
            i = j;
        }
    }
    return tokens;
}

function getHighlightedMarkdown(oldText, newText) {
    if (!oldText && !newText) return '';

    const oldTokens = tokenizeHtmlForDiff(oldText || '');
    const newTokens = tokenizeHtmlForDiff(newText || '');

    const diffParts = diffArrays(oldTokens, newTokens, {
        comparator: (left, right) => {
            if (left.type !== right.type) return false;
            if (left.type === 'tag') {
                return normalizeTag(left.value) === normalizeTag(right.value);
            }
            return left.value === right.value;
        }
    });

    let highlightedContent = '';

    diffParts.forEach(part => {
        if (part.added) {
            let currentSpan = '';
            part.value.forEach(tok => {
                if (tok.type === 'tag') {
                    if (currentSpan) {
                        const tempText = currentSpan.replace(/&#?[a-zA-Z0-9]+;/g, 'X');
                        const hasAlpha = /[\p{L}\p{N}]/u.test(tempText);
                        highlightedContent += hasAlpha ? `<span class="diff-added">${currentSpan}</span>` : currentSpan;
                        currentSpan = '';
                    }
                    highlightedContent += tok.value;
                } else {
                    currentSpan += escapeHtml(tok.value);
                }
            });
            if (currentSpan) {
                const tempText = currentSpan.replace(/&#?[a-zA-Z0-9]+;/g, 'X');
                const hasAlpha = /[\p{L}\p{N}]/u.test(tempText);
                highlightedContent += hasAlpha ? `<span class="diff-added">${currentSpan}</span>` : currentSpan;
            }
        } else if (!part.removed) {
            part.value.forEach(tok => {
                highlightedContent += tok.value;
            });
        }
    });

    return highlightedContent;
}
// ────────────────────────────────────────────────────────────────────────────

const VersionComparisonModal = ({
    isOpen,
    onClose,
    comparisonData,
    activeSection,
    versionsList
}) => {
    const [viewMode, setViewMode] = React.useState('highlights');
    const leftScrollRef = React.useRef(null);
    const rightScrollRef = React.useRef(null);

    const handleScroll = (source, e) => {
        if (source === 'left' && rightScrollRef.current) {
            if (rightScrollRef.current.scrollTop !== e.target.scrollTop) {
                rightScrollRef.current.scrollTop = e.target.scrollTop;
            }
        } else if (source === 'right' && leftScrollRef.current) {
            if (leftScrollRef.current.scrollTop !== e.target.scrollTop) {
                leftScrollRef.current.scrollTop = e.target.scrollTop;
            }
        }
    };

    if (!isOpen || !comparisonData) return null;

    const { v1, v1_content, v2, v2_content } = comparisonData;

    const getAuthorForVersion = (vNum) => {
        const v = versionsList?.find(ver => ver.version_number === vNum);
        return v?.created_by_name || 'System';
    };

    // Determine which version is older and which is newer for correct column order.
    // Left = older version, Right = newer version.
    const oldVersion = v1 < v2 ? v1 : v2;
    const oldContent = v1 < v2 ? v1_content : v2_content;
    const newVersion = v1 < v2 ? v2 : v1;
    const newContent = v1 < v2 ? v2_content : v1_content;

    return (
        <div className="compare-overlay">
            <div className="compare-modal-container">
                <div className="compare-header">
                    <div className="compare-header-info">
                        <div>
                            <h3>Compare Versions</h3>
                            <p>{activeSection}</p>
                        </div>
                    </div>
                    <div className="compare-header-right">
                        <div className="mode-toggle">
                            <span className={viewMode === 'plain' ? 'active' : ''}>Plain</span>
                            <div
                                className={`toggle-track ${viewMode === 'highlights' ? 'on' : ''}`}
                                onClick={() => setViewMode(viewMode === 'plain' ? 'highlights' : 'plain')}
                            >
                                <div className="toggle-thumb"></div>
                            </div>
                            <span className={viewMode === 'highlights' ? 'active' : ''}>Highlights</span>
                        </div>
                        <button className="compare-close-btn" onClick={onClose}>
                            <X size={20} />
                        </button>
                    </div>
                </div>

                <div className="compare-body">
                    {/* LEFT — Older version (plain parsed) */}
                    <div className="compare-column left">
                        <div className="column-label-header">
                            <span className="v-tag-badge">VERSION {oldVersion}</span>
                            <span className="v-author-name">{getAuthorForVersion(oldVersion)}</span>
                        </div>
                        <div
                            className="column-content final-view-area"
                            ref={leftScrollRef}
                            onScroll={(e) => handleScroll('left', e)}
                            dangerouslySetInnerHTML={{ __html: oldContent }}
                        />
                    </div>

                    {/* RIGHT — Newer version (with diff highlights) */}
                    <div className="compare-column right">
                        <div className="column-label-header">
                            <span className="v-tag-badge">VERSION {newVersion}</span>
                            <span className="v-author-name">{getAuthorForVersion(newVersion)}</span>
                        </div>
                        <div
                            className="column-content final-view-area"
                            ref={rightScrollRef}
                            onScroll={(e) => handleScroll('right', e)}
                            dangerouslySetInnerHTML={{
                                __html: viewMode === 'highlights'
                                    ? getHighlightedMarkdown(oldContent, newContent)
                                    : newContent
                            }}
                        />
                    </div>
                </div>

                <div className="compare-footer">
                    <button className="done-btn-main" onClick={onClose}>Done</button>
                </div>
            </div>
        </div>
    );
};

export default VersionComparisonModal;
